import { useState } from 'react';

import { Button } from '../../button';

import {
  assertions,
  caseLabels,
  CASES,
  danger,
  DEFENSE_COUNTEREXAMPLE,
  DEFENSE_REFINEMENT,
  defenses,
  defenseSummary,
  defenseTransition,
  FLOW_QUESTIONS,
  FLOW_REFINEMENT,
  flowLabels,
  flowPiecesText,
  flowRun,
  flowSummary,
  flowTransition,
  initialDefense,
  initialFlow,
  initialTest,
  initialTrifecta,
  LOWERS_ODDS,
  NO_PROPOSALS,
  onCount,
  passedText,
  plantedClaimPresent,
  plantLabels,
  PLANTS,
  TEST_COUNTEREXAMPLE,
  TEST_REFINEMENT,
  testSummary,
  testTransition,
  THINGS,
  thingLabels,
  toCase,
  TODAY,
  toFlowQuestion,
  toPlant,
  trials,
  TRIFECTA_REFINEMENT,
  trifectaSummary,
  trifectaTransition,
  type DefenseAction,
  type FlowAction,
  type TestAction,
  type TrifectaAction,
} from './inject-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './inject-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const REPLAY = 'This graphic replays real runs.';

export function InjectFlowLab() {
  const [state, setState] = useState(initialFlow);
  const act = (action: FlowAction) => setState((current) => flowTransition(current, action));
  const run = flowRun(state.question, state.forced);
  const summary = flowSummary(state);
  return (
    <Lab
      title="What happens when the planted piece is among the pieces?"
      eyebrow="Injection · when it gets through anyway"
      className="rel-lab inject-lab"
    >
      <Predict>
        What happens when the planted piece is among the pieces? Choose a Question, then press Ask.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toFlowQuestion(value) })}
            options={FLOW_QUESTIONS.map((id) => ({ value: id, label: flowLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.forced}
          onClick={() => act({ type: 'toggle-force' })}
        >
          Force the planted piece in
        </Button>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="Pieces sent">
        <pre aria-label="Pieces sent">{state.asked ? flowPiecesText(run) : '(not asked yet)'}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="The answer">
          <pre aria-label="The answer">{state.asked ? run.answer : '(not asked yet)'}</pre>
        </Panel>
        <Panel label="Proposals">
          <pre aria-label="Proposals">{state.asked ? NO_PROPOSALS : '(not asked yet)'}</pre>
        </Panel>
      </div>
      {state.asked ? (
        <Facts
          values={[
            ['Proposals', '0'],
            ['Writes', '0'],
            ['Planted claim in the answer', plantedClaimPresent(run.answer)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.asked ? <p className="rel-caption">{FLOW_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function InjectTrifectaLab() {
  const [state, setState] = useState(initialTrifecta);
  const act = (action: TrifectaAction) =>
    setState((current) => trifectaTransition(current, action));
  const level = danger(state);
  const summary = trifectaSummary(state);
  return (
    <Lab
      title="Which three things make an injection dangerous?"
      eyebrow="Injection · three things together"
      className="rel-lab inject-lab"
    >
      <Predict>
        Which three things make an injection dangerous? Press the three toggles one at a time.
      </Predict>
      <div className="cb-controls">
        {THINGS.map((thing) => (
          <Button
            key={thing}
            size="sm"
            variant="secondary"
            aria-pressed={state[thing]}
            onClick={() => act({ type: 'toggle', value: thing })}
          >
            {thingLabels[thing]}
          </Button>
        ))}
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Facts
        values={[
          ['Switched on', `${onCount(state)} of 3`],
          ['Danger', level],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      <Table
        label="What the assistant has today"
        columns={['Thing', 'Today']}
        template="minmax(0, 1fr) minmax(0, 2fr)"
        rows={TODAY.map(([thing, today]) => [thing, today])}
      />
      {level === 'full' ? <p className="rel-caption">{TRIFECTA_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function InjectDefenseLab() {
  const [state, setState] = useState(initialDefense);
  const act = (action: DefenseAction) => setState((current) => defenseTransition(current, action));
  const defense = defenses[state.plant];
  const summary = defenseSummary(state);
  return (
    <Lab
      title="Which defense catches this planted instruction?"
      eyebrow="Injection · the defenses are already built"
      className="rel-lab inject-lab"
    >
      <Predict>
        Which defense catches this planted instruction? Choose a Planted instruction, then press
        Check.
      </Predict>
      <div className="cb-controls">
        <label>
          Planted instruction
          <ModelSelect
            aria-label="Planted instruction"
            value={state.plant}
            onValueChange={(value) => act({ type: 'plant', value: toPlant(value) })}
            options={PLANTS.map((plant) => ({ value: plant, label: plantLabels[plant] }))}
          />
        </label>
        <Button size="sm" disabled={state.checked} onClick={() => act({ type: 'check' })}>
          Check
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="What stops it">
        <pre aria-label="What stops it">{state.checked ? defense.stops : '(not checked yet)'}</pre>
      </Panel>
      <Panel label="What only lowers the odds">
        <pre aria-label="What only lowers the odds">
          {state.checked ? LOWERS_ODDS : '(not checked yet)'}
        </pre>
      </Panel>
      {state.checked ? (
        <Facts
          values={[
            ['Defense', defense.name],
            ['Lives in', defense.livesIn],
            ['The data sentence', 'lowers the odds only'],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.checked ? (
        <p className="rel-caption">
          {DEFENSE_COUNTEREXAMPLE} {DEFENSE_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function InjectTestLab() {
  const [state, setState] = useState(initialTest);
  const act = (action: TestAction) => setState((current) => testTransition(current, action));
  const summary = testSummary(state);
  return (
    <Lab
      title="What does the injection test assert?"
      eyebrow="Injection · test it like a bug"
      className="rel-lab inject-lab"
    >
      <Predict>
        What does the injection test assert? Choose a Case, then press Run the test.
      </Predict>
      <div className="cb-controls">
        <label>
          Case
          <ModelSelect
            aria-label="Case"
            value={state.case}
            onValueChange={(value) => act({ type: 'case', value: toCase(value) })}
            options={CASES.map((entry) => ({ value: entry, label: caseLabels[entry] }))}
          />
        </label>
        <Button size="sm" disabled={state.run} onClick={() => act({ type: 'run' })}>
          Run the test
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="Assertions"
        columns={['Assertion', 'Expected', 'Outcome']}
        template="minmax(0, 1.4fr) minmax(0, 1.6fr) minmax(0, 0.6fr)"
        rows={
          state.run
            ? assertions[state.case].map((row) => [row.name, row.recorded, row.outcome])
            : []
        }
      />
      {state.run ? (
        <Facts
          values={[
            ['Trials', String(trials[state.case])],
            ['Assertions', String(assertions[state.case].length)],
            ['Passed', passedText(state.case)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.run ? (
        <p className="rel-caption">
          {TEST_COUNTEREXAMPLE} {TEST_REFINEMENT}
        </p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
